// Role x permission matrix — interactive editor.
import { useEffect, useMemo, useState } from "react";
import { api } from "../../../shared/api/client";
import { Empty, Loading } from "../../../shared/components/Empty";
import { Pill } from "../../../shared/components/Pill";
import { useToasts } from "../../../shared/stores/toasts";

type Permission = { id: number; code: string; resource: string; action: string; description?: string };
type Role = { id: number; code: string; name: string; description?: string; is_system: boolean; permissions: Permission[] };

// 조직 위계 8개 역할 (대표 → 게스트). 나머지는 "시스템·기능 역할"로 분리.
const PRIMARY = ["CEO", "DIVISION_HEAD", "MANAGER", "TEAM_LEAD", "EMPLOYEE", "INTERN", "CLIENT", "GUEST"];
const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: "최고관리자", CEO: "대표", C_LEVEL: "임원", DIVISION_HEAD: "실장", MANAGER: "부서장",
  TEAM_LEAD: "팀장", HR_ADMIN: "인사관리자", FINANCE_ADMIN: "재무관리자", EMPLOYEE: "사원", INTERN: "인턴", CLIENT: "클라이언트", GUEST: "게스트",
};
const ROLE_RANK: Record<string, number> = {
  GUEST: 0, CLIENT: 5, INTERN: 10, EMPLOYEE: 20, TEAM_LEAD: 30, MANAGER: 40,
  FINANCE_ADMIN: 50, HR_ADMIN: 50, DIVISION_HEAD: 60, C_LEVEL: 80, CEO: 90, SUPER_ADMIN: 100,
};
const ROLE_COLOR: Record<string, string> = {
  SUPER_ADMIN: "#111827", CEO: "#9333ea", C_LEVEL: "#7c3aed", DIVISION_HEAD: "#dc2626", MANAGER: "#0891b2",
  TEAM_LEAD: "#2563eb", FINANCE_ADMIN: "#b45309", HR_ADMIN: "#b45309", EMPLOYEE: "#16a34a", INTERN: "#65a30d", CLIENT: "#6b7280", GUEST: "#9ca3af",
};
const byRankDesc = (a: Role, b: Role) => (ROLE_RANK[b.code] ?? 0) - (ROLE_RANK[a.code] ?? 0);

export function AdminRoles() {
  const [showSystem, setShowSystem] = useState(false);
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [perms, setPerms] = useState<Permission[] | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const toast = useToasts((s) => s.push);

  useEffect(() => {
    Promise.all([
      api.get<Role[]>("/iam/roles"),
      api.get<Permission[]>("/iam/permissions"),
    ])
      .then(([rRes, pRes]) => {
        setRoles(rRes.data);
        setPerms(pRes.data);
        const ordered = [...rRes.data].sort(byRankDesc);
        const first = ordered.find((r) => PRIMARY.includes(r.code)) ?? ordered[0];
        if (first) {
          setSelectedRoleId(first.id);
          setDraft(new Set(first.permissions.map((p) => p.code)));
        }
      })
      .catch(() => {
        setRoles([]);
        setPerms([]);
      });
  }, []);

  const selected = roles?.find((r) => r.id === selectedRoleId) ?? null;
  const dirty = useMemo(() => {
    if (!selected) return false;
    const current = new Set(selected.permissions.map((p) => p.code));
    if (current.size !== draft.size) return true;
    for (const c of current) if (!draft.has(c)) return true;
    return false;
  }, [selected, draft]);

  const groupedPerms = useMemo(() => {
    if (!perms) return {};
    const g: Record<string, Permission[]> = {};
    for (const p of perms) {
      g[p.resource] ??= [];
      g[p.resource].push(p);
    }
    return g;
  }, [perms]);

  const onSelectRole = (r: Role) => {
    setSelectedRoleId(r.id);
    setDraft(new Set(r.permissions.map((p) => p.code)));
  };

  const toggle = (code: string) => {
    setDraft((d) => {
      const next = new Set(d);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleResource = (resource: string, checked: boolean) => {
    setDraft((d) => {
      const next = new Set(d);
      for (const p of groupedPerms[resource] ?? []) {
        if (checked) next.add(p.code);
        else next.delete(p.code);
      }
      return next;
    });
  };

  const reset = () => {
    if (!selected) return;
    setDraft(new Set(selected.permissions.map((p) => p.code)));
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await api.put<Role>(`/iam/roles/${selected.id}/permissions`, {
        permission_codes: Array.from(draft),
      });
      setRoles((rs) => rs?.map((r) => (r.id === selected.id ? res.data : r)) ?? rs);
      toast({ kind: "success", title: "권한 저장됨", desc: `${selected.name} (${draft.size}개 권한)` });
    } catch (e: any) {
      toast({ kind: "error", title: "저장 실패", desc: e?.response?.data?.detail ?? "" });
    } finally {
      setSaving(false);
    }
  };

  if (roles === null || perms === null) return <Loading/>;
  if (roles.length === 0) return <Empty msg="역할이 없습니다"/>;

  const primary = [...roles].filter((r) => PRIMARY.includes(r.code)).sort(byRankDesc);
  const system = [...roles].filter((r) => !PRIMARY.includes(r.code)).sort(byRankDesc);
  const renderRow = (r: Role) => {
    const active = r.id === selectedRoleId;
    return (
      <button key={r.id} onClick={() => onSelectRole(r)}
        style={{ width: "100%", textAlign: "left", border: 0, cursor: "pointer", borderRadius: 8, marginBottom: 2,
          display: "flex", alignItems: "center", gap: 9, padding: "7px 9px",
          background: active ? "rgba(99,102,241,0.12)" : "transparent" }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", flexShrink: 0, background: ROLE_COLOR[r.code] ?? "#6b7280" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: active ? 700 : 500, color: active ? "var(--p-600,#4f46e5)" : "var(--text-1)" }}>{ROLE_LABEL[r.code] ?? r.name}</div>
          <div className="mono" style={{ fontSize: 10, color: "var(--text-3)" }}>{r.code}</div>
        </div>
        <span style={{ fontSize: 10.5, color: "var(--text-3)", whiteSpace: "nowrap" }} title="권한 수">{r.permissions.length}</span>
      </button>
    );
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "230px 1fr", gap: 16, alignItems: "start" }}>
      {/* Left: 조직 역할 8개 + 시스템·기능 역할(접이식) */}
      <div className="t-card" style={{ padding: 10, position: "sticky", top: 0, alignSelf: "start" }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text-3)", padding: "2px 4px 7px", textTransform: "uppercase", letterSpacing: "0.05em" }}>조직 역할</div>
        {primary.map(renderRow)}
        {system.length > 0 && (
          <>
            <button onClick={() => setShowSystem((s) => !s)}
              style={{ width: "100%", textAlign: "left", border: 0, cursor: "pointer", background: "transparent",
                fontSize: 10.5, fontWeight: 700, color: "var(--text-3)", padding: "10px 4px 7px", textTransform: "uppercase", letterSpacing: "0.05em",
                borderTop: "1px solid var(--border)", marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
              시스템 · 기능 역할 ({system.length}) <span style={{ marginLeft: "auto" }}>{showSystem ? "▲" : "▼"}</span>
            </button>
            {showSystem && system.map(renderRow)}
          </>
        )}
      </div>

      {/* Right: permission matrix */}
      {selected && (
        <div className="t-grid cols-1">
          <div className="t-card">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ width: 11, height: 11, borderRadius: "50%", flexShrink: 0, background: ROLE_COLOR[selected.code] ?? "#6b7280" }} />
              <h3 style={{ margin: 0 }}>{ROLE_LABEL[selected.code] ?? selected.name}</h3>
              <span className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>{selected.code}</span>
              <span style={{ fontSize: 11, color: "var(--text-3)" }}>등급 {ROLE_RANK[selected.code] ?? 0}</span>
              {!PRIMARY.includes(selected.code) && <Pill kind="violet">시스템</Pill>}
              <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-3)" }}>
                {draft.size} / {perms.length} 권한
              </div>
            </div>
            {selected.description && (
              <p style={{ margin: 0, fontSize: 12, color: "var(--text-3)" }}>{selected.description}</p>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                className="t-btn solid"
                onClick={save}
                disabled={!dirty || saving}
                style={{ opacity: !dirty || saving ? 0.5 : 1 }}
              >
                {saving ? "저장 중…" : "저장"}
              </button>
              <button
                className="t-btn"
                onClick={reset}
                disabled={!dirty || saving}
                style={{ opacity: !dirty || saving ? 0.5 : 1 }}
              >
                되돌리기
              </button>
              {dirty && <span style={{ fontSize: 12, color: "var(--warn)", alignSelf: "center" }}>변경됨</span>}
            </div>
          </div>

          {Object.entries(groupedPerms).map(([resource, items]) => {
            const allChecked = items.every((p) => draft.has(p.code));
            const someChecked = items.some((p) => draft.has(p.code));
            return (
              <div key={resource} className="t-card">
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                    onChange={(e) => toggleResource(resource, e.target.checked)}
                  />
                  <span style={{ fontWeight: 600, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {resource}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                    {items.filter((p) => draft.has(p.code)).length} / {items.length}
                  </span>
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 6 }}>
                  {items.map((p) => (
                    <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 4, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={draft.has(p.code)}
                        onChange={() => toggle(p.code)}
                      />
                      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                        <span className="mono" style={{ fontSize: 11 }}>{p.action}</span>
                        {p.description && <span style={{ fontSize: 11, color: "var(--text-3)" }}>{p.description}</span>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
